import axios from 'axios'
import { jwtDecode } from 'jwt-decode'

import config from '../config/env'
import { store } from '../main'
import { setSnackBarStates } from '../features/ui/uiSlice'
import cookieUtils from '../utils/cookieUtils'

/**
 * Token refresh timer
 */
let refreshTimer = null

/**
 * Schedule automatic token refresh before expiry
 * @param {string} accessToken - The access token to check expiry
 */
const scheduleTokenRefresh = (accessToken) => {
  // Clear any existing timer
  if (refreshTimer) {
    clearTimeout(refreshTimer)
    refreshTimer = null
  }

  if (!accessToken) {
    return
  }

  let payload
  try {
    payload = jwtDecode(accessToken)
  } catch (error) {
    console.error('Failed to decode JWT:', error)
    return
  }

  if (!payload || !payload.exp) {
    console.warn('Token has no expiry, cannot schedule refresh')
    return
  }

  // Calculate time until expiry (in milliseconds)
  const expiryTime = payload.exp * 1000 // Convert to milliseconds
  const currentTime = Date.now()
  const timeUntilExpiry = expiryTime - currentTime

  // Refresh 60 seconds before expiry (or immediately if less than 60 seconds left)
  const refreshBuffer = 60 * 1000 // 60 seconds
  const timeUntilRefresh = Math.max(timeUntilExpiry - refreshBuffer, 0)

  // console.log(`Token expires in ${Math.round(timeUntilExpiry / 1000)} seconds. Scheduling refresh in ${Math.round(timeUntilRefresh / 1000)} seconds.`)

  refreshTimer = setTimeout(async () => {
    // console.log('Automatically refreshing token...')
    const refreshToken = cookieUtils.get('refreshToken')

    if (!refreshToken) {
      console.warn('No refresh token available for automatic refresh')
      return
    }

    try {
      const response = await apiClient.post('/token/refresh', { token: refreshToken })
      const { accessToken: newAccessToken } = response.data

      if (!newAccessToken) {
        throw new Error('No access token received')
      }

      // Update stored access token (refresh token remains the same)
      cookieUtils.set('authToken', newAccessToken, null)

      // console.log('Token refreshed successfully')

      // Schedule next refresh
      scheduleTokenRefresh(newAccessToken)
    } catch (error) {
      console.error('Failed to refresh token:', error)

      // Clear auth and redirect to login
      cookieUtils.delete('authToken')
      cookieUtils.delete('refreshToken')
      cookieUtils.delete('authUser')

      store.dispatch(setSnackBarStates({
        message: 'Session expired. Please login again.',
        severity: 'error',
        open: true,
        autoHideDuration: 8000
      }))

      setTimeout(() => {
        window.location.href = '/login'
      }, 500)
    }
  }, timeUntilRefresh)
}

/**
 * Clear token refresh timer
 */
const clearTokenRefreshTimer = () => {
  if (refreshTimer) {
    clearTimeout(refreshTimer)
    refreshTimer = null
  }
}

/**
 * Create axios instance with base configuration
 * 
 * Custom config options (pass in request config):
 * - showSnackbar: boolean - Show snackbar for errors (default: false)
 * - showSuccessSnackbar: boolean - Show snackbar for successful responses (default: false)
 * - successMessage: string - Custom success message (defaults to response.data.message)
 * - errorMessage: string - Custom error message (defaults to error.response.data.message)
 * - autoHideDuration: number - Snackbar auto hide duration in ms (default: 3000 success, 8000 error)
 */
const apiClient = axios.create({
  baseURL: config.API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds
})

/**
 * Request interceptor
 * Adds authentication token to requests if available from cookies
 */
apiClient.interceptors.request.use((requestConfig) => {
  const token = cookieUtils.get('authToken')
  if (token) {
    requestConfig.headers.Authorization = `Bearer ${token}`
  }

  // Add API key header if available
  if (config.API_KEY) {
    requestConfig.headers['x-api-key'] = config.API_KEY
  }

  return requestConfig
}, (error) => {
  return Promise.reject(error)
}
)

/**
 * Response interceptor
 * Handles successful responses and error responses with optional snackbar notifications
 */
apiClient.interceptors.response.use((response) => {
  const { showSnackbar, showSuccessSnackbar, successMessage, autoHideDuration } = response.config

  // Show success snackbar if configured
  if (showSnackbar && showSuccessSnackbar) {
    const message = successMessage || response.data?.message || 'Operation completed successfully'

    store.dispatch(setSnackBarStates({
      message: message,
      severity: 'success',
      open: true,
      autoHideDuration: autoHideDuration ?? 3000
    }))
  }
  return response
}, (error) => {
  const { showSnackbar, errorMessage, autoHideDuration } = error.config || {}
  let displayMessage = errorMessage || 'An error occurred'

  if (error.response) {
    // Server responded with error status
    const { status, data } = error.response

    // Extract error message from response (only if it's a non-empty string)
    const responseMessage = (typeof data?.message === 'string' && data.message.trim()) ? data.message : null
    displayMessage = responseMessage || errorMessage || `Error: ${status}`

    // Handle 401 Unauthorized - session expired
    if (status === 401) {
      displayMessage = 'Session expired. Please login again.'
      cookieUtils.delete('authToken')
      cookieUtils.delete('refreshToken')
      cookieUtils.delete('authUser')
      clearTokenRefreshTimer()

      // Show snackbar before redirect if configured
      if (showSnackbar) {
        store.dispatch(setSnackBarStates({
          message: displayMessage,
          severity: 'error',
          open: true,
          autoHideDuration: autoHideDuration ?? 8000
        }))
      }

      // Delay redirect to allow snackbar to show
      setTimeout(() => {
        window.location.href = '/login'
      }, 500)

      return Promise.reject(error)
    }

    // Check for token expiration in message (fallback)
    if (typeof displayMessage === 'string' && displayMessage.toLowerCase().includes('token expired')) {
      displayMessage = 'Session expired. Please login again.'
      cookieUtils.delete('authToken')
      cookieUtils.delete('refreshToken')
      cookieUtils.delete('authUser')
      clearTokenRefreshTimer()

      if (showSnackbar) {
        store.dispatch(setSnackBarStates({
          message: displayMessage,
          severity: 'error',
          open: true,
          autoHideDuration: autoHideDuration ?? 8000
        }))
      }

      setTimeout(() => {
        window.location.href = '/login'
      }, 500)

      return Promise.reject(error)
    }

    switch (status) {
      case 400:
        // Bad request
        console.error('Bad request:', data)
        break
      case 500:
        // Server error
        console.error('Server error:', data)
        break
      default:
        console.error('API error:', error.response)
    }
  } else if (error.request) {
    // Request made but no response received
    console.error('Network error:', error.request)
    displayMessage = errorMessage || 'Network error. Please check your connection.'
  } else {
    // Something else happened
    console.error('Error:', error.message)
    displayMessage = errorMessage || error.message || 'An unexpected error occurred'
  }

  // Show error snackbar if configured
  if (showSnackbar) {
    // Ensure message is a string
    const finalMessage = typeof displayMessage === 'string' ? displayMessage : 'An unexpected error occurred'
    
    store.dispatch(setSnackBarStates({
      message: finalMessage,
      severity: 'error',
      open: true,
      autoHideDuration: autoHideDuration ?? 8000
    }))
  }

  return Promise.reject(error)
}
)

export { scheduleTokenRefresh, clearTokenRefreshTimer }
export default apiClient
